import { useContext, useEffect, useMemo, useState } from "react";
import { useAccess } from "../../../../access/hooks/useAccess";
import { billingAllows } from "../../billingAccess";
import { Button, Input, Select, Tags } from "../../../ui";
import { Context } from "../../../../context/Context";
import { formatSar } from "../../../../utils/billingMoney";
import {
  downloadInvoicePdfAPI,
  getInvoiceAnalyticsAPI,
  getInvoiceLedgerAPI,
  getInvoiceAPI,
  listBillingStoresAPI,
  listInvoicesAPI,
} from "../../../../utils/api/billing";
import InvoiceDetailModal from "../modals/InvoiceDetailModal";
import { useBillingAdminText } from "../billingAdminI18n";

const STATUS_ORDER = ["awaiting_payment", "finance_review", "paid", "cancelled"];
const STATUS_BADGE = {
  awaiting_payment: "warning",
  finance_review: "warning",
  paid: "success",
  cancelled: "danger",
};

const DEFAULT_FILTERS = {
  period: "all",
  from: "",
  to: "",
  storeId: "",
  revenueType: "",
  status: "",
  search: "",
};

const formatDate = (value, lang) =>
  value ? new Date(value).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US") : "-";

function periodToRange(period, from, to) {
  if (period === "custom") return { from: from || undefined, to: to || undefined };
  const now = new Date();
  if (period === "30") {
    const start = new Date(now);
    start.setDate(start.getDate() - 30);
    return { from: start.toISOString().slice(0, 10) };
  }
  if (period === "90") {
    const start = new Date(now);
    start.setDate(start.getDate() - 90);
    return { from: start.toISOString().slice(0, 10) };
  }
  if (period === "ytd") {
    return { from: new Date(Date.UTC(now.getUTCFullYear(), 0, 1)).toISOString().slice(0, 10) };
  }
  return {};
}

const InvoicesTab = ({ access = null } = {}) => {
  const { lang = "en" } = useContext(Context) || {};
  const { can } = useAccess();
  const text = useBillingAdminText(lang);
  const token = localStorage.getItem("auth-token");
  const canView = billingAllows(can("billing.admin.view"), access?.canViewBillingAdmin);

  const [stores, setStores] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [analytics, setAnalytics] = useState(null);
  const [ledger, setLedger] = useState({ data: [], pagination: { page: 1, pages: 1, total: 0 } });
  const [openGroups, setOpenGroups] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detailInvoice, setDetailInvoice] = useState(null);
  const [downloadingId, setDownloadingId] = useState("");

  useEffect(() => {
    if (!canView || !token) return;
    listBillingStoresAPI(token)
      .then((result) => setStores(result.data || []))
      .catch(() => {});
  }, [canView, token]);

  const runFilters = async (overrides = {}) => {
    if (!canView || !token) return;
    const nextFilters = { ...filters, ...overrides };
    setFilters(nextFilters);
    setLoading(true);
    setError("");
    const range = periodToRange(nextFilters.period, nextFilters.from, nextFilters.to);
    const analyticsQuery = {
      from: range.from,
      to: range.to,
      storeId: nextFilters.storeId,
      revenueType: nextFilters.revenueType,
      search: nextFilters.search,
    };
    const ledgerQuery = { ...analyticsQuery, status: nextFilters.status, page: overrides.page || 1, limit: 20 };
    try {
      const [analyticsResult, ledgerResult] = await Promise.all([
        getInvoiceAnalyticsAPI(token, analyticsQuery),
        getInvoiceLedgerAPI(token, ledgerQuery),
      ]);
      setAnalytics(analyticsResult.data);
      setLedger(ledgerResult);
      setOpenGroups(new Set(ledgerResult.data?.[0] ? [ledgerResult.data[0].storeId] : []));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const resetFilters = () => runFilters({ ...DEFAULT_FILTERS });

  const toggleGroup = (storeId) => {
    setOpenGroups((current) => {
      const next = new Set(current);
      if (next.has(storeId)) next.delete(storeId);
      else next.add(storeId);
      return next;
    });
  };

  const viewMore = async (group) => {
    try {
      const result = await listInvoicesAPI(token, { storeId: group.storeId, limit: 100 });
      setLedger((current) => ({
        ...current,
        data: current.data.map((row) =>
          row.storeId === group.storeId ? { ...row, invoices: result.data || [], hasMore: false } : row,
        ),
      }));
    } catch (err) {
      setError(err.message);
    }
  };

  const openDetail = async (invoiceId) => {
    setError("");
    try {
      const result = await getInvoiceAPI(token, invoiceId);
      setDetailInvoice(result.data);
    } catch (err) {
      setError(err.message);
    }
  };

  const exportPdf = async (invoice) => {
    setError("");
    setDownloadingId(invoice._id);
    try {
      await downloadInvoicePdfAPI(token, invoice._id, lang);
    } catch (err) {
      setError(err.message || text.invoices.exportPdfError);
    } finally {
      setDownloadingId("");
    }
  };

  const restaurantOptions = useMemo(
    () => [
      { value: "", label: text.invoices.filters.allRestaurants },
      ...stores.map((store) => ({ value: store.id, label: `${store.businessName} (${store.id})` })),
    ],
    [stores, text],
  );

  if (!canView) {
    return <div className="billing-admin-denied">{text.invoices.denied}</div>;
  }

  const statusCounts = analytics?.byStatus || {};
  const revenueByType = analytics?.revenueByType || [];
  const timeline = analytics?.monthlyTimeline || [];
  const maxTimelineAmount = Math.max(1, ...timeline.map((row) => row.collectedMinor));

  return (
    <section className="billing-invoices-v141">
      <div className="billing-filter-bar">
        <Select
        fullWidth={false}
        label={text.invoices.filters.period}
        value={filters.period}
        onChange={(event) => runFilters({ period: event.target.value, page: 1 })}
          options={[
            { value: "all", label: text.invoices.filters.periodAll },
            { value: "30", label: text.invoices.filters.period30 },
            { value: "90", label: text.invoices.filters.period90 },
            { value: "ytd", label: text.invoices.filters.periodYtd },
            { value: "custom", label: text.invoices.filters.periodCustom },
          ]}
        />
        {filters.period === "custom" && (
          <>
            <Input
          fullWidth={false}
          type="date"
          label={text.invoices.filters.from}
          value={filters.from}
          onChange={(event) => setFilters((current) => ({ ...current, from: event.target.value }))}
          onBlur={() => runFilters({ page: 1 })}
          />
            <Input
              fullWidth={false}
              type="date"
              label={text.invoices.filters.to}
              value={filters.to}
              onChange={(event) => setFilters((current) => ({ ...current, to: event.target.value }))}
              onBlur={() => runFilters({ page: 1 })}
              />
          </>
        )}
        <Select
          fullWidth={false}
          label={text.invoices.filters.restaurant}
          value={filters.storeId}
          onChange={(event) => runFilters({ storeId: event.target.value, page: 1 })}
          options={restaurantOptions}
          />
        <Select
          fullWidth={false}
          label={text.invoices.filters.revenueType}
          value={filters.revenueType}
          onChange={(event) => runFilters({ revenueType: event.target.value, page: 1 })}
          options={[
            { value: "", label: text.invoices.filters.allRevenueTypes },
            { value: "LUD", label: text.invoices.filters.revenueTypeLud },
            { value: "INTG", label: text.invoices.filters.revenueTypeIntg },
          ]}
          />
        <Select
          fullWidth={false}
          label={text.invoices.filters.invoiceStatus}
          value={filters.status}
          onChange={(event) => runFilters({ status: event.target.value, page: 1 })}
          options={[
            { value: "", label: text.invoices.filters.allInvoiceStatuses },
            ...STATUS_ORDER.map((key) => ({ value: key, label: text.invoices.statusCards[key] })),
          ]}
          />
        <Input
          fullWidth={false}
          label={text.common.search}
          value={filters.search}
          onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
          onKeyDown={(event) => {
            if (event.key === "Enter") runFilters({ page: 1 });
          }}
          placeholder={text.invoices.filters.searchPlaceholder}
        />
        <Button appearance="ghost" onClick={resetFilters}>
          {text.invoices.filters.reset}
        </Button>
      </div>

      {error && <div className="billing-error">{error}</div>}

      <div className="billing-kpi-row">
        <div className="billing-kpi-card">
          <span>{text.invoices.kpis.totalInvoiced}</span>
          <strong>{formatSar(analytics?.totals?.totalInvoicedMinor || 0, lang)}</strong>
          <small>{text.invoices.kpis.totalInvoicedHint}</small>
        </div>
        <div className="billing-kpi-card">
          <span>{text.invoices.kpis.collected}</span>
          <strong>{formatSar(analytics?.totals?.collectedMinor || 0, lang)}</strong>
          <small>{text.invoices.kpis.collectedHint}</small>
        </div>
        <div className="billing-kpi-card">
          <span>{text.invoices.kpis.outstanding}</span>
          <strong>{formatSar(analytics?.totals?.outstandingMinor || 0, lang)}</strong>
          <small>{text.invoices.kpis.outstandingHint}</small>
        </div>
        <div className="billing-kpi-card">
          <span>{text.invoices.kpis.invoiceCount}</span>
          <strong>{analytics?.totals?.invoiceCount || 0}</strong>
        </div>
      </div>

      <div className="billing-analytics-grid">
        <div className="billing-analytics-box">
          <h4>{text.invoices.analytics.revenueByType}</h4>
          {revenueByType.length ? (
            revenueByType.map((row) => (
              <div key={row._id} className="billing-analytics-type-row">
                <span>{row._id === "LUD" ? text.invoices.filters.revenueTypeLud : row._id === "INTG" ? text.invoices.filters.revenueTypeIntg : row._id}</span>
                <strong>{formatSar(row.collectedMinor, lang)}</strong>
                <small>{formatSar(row.invoicedMinor, lang)}</small>
              </div>
            ))
          ) : (
            <div className="billing-empty-note">{text.invoices.analytics.empty}</div>
          )}
        </div>
        <div className="billing-analytics-box">
          <h4>{text.invoices.analytics.timeline}</h4>
          {timeline.length ? (
            timeline
              .slice()
              .reverse()
              .map((row) => (
                <div key={row._id} className="billing-timeline-row">
                  <span>{row._id}</span>
                  <div className="billing-timeline-track">
                    <div
                      className="billing-timeline-fill"
                      style={{ width: `${Math.max(2, (row.collectedMinor / maxTimelineAmount) * 100)}%` }}
                    />
                  </div>
                  <strong>{formatSar(row.collectedMinor, lang)}</strong>
                </div>
              ))
          ) : (
            <div className="billing-empty-note">{text.invoices.analytics.timelineEmpty}</div>
          )}
        </div>
      </div>

      <div className="billing-status-cards">
        {STATUS_ORDER.map((key) => (
          <button
            key={key}
            type="button"
            className={`billing-status-card ${filters.status === key ? "active" : ""}`}
            onClick={() => runFilters({ status: filters.status === key ? "" : key, page: 1 })}
          >
            <span>{text.invoices.statusCards[key]}</span>
            <strong>{statusCounts[key]?.count ?? 0}</strong>
            <small>{text.invoices.statusCards[`${key}Hint`]}</small>
          </button>
        ))}
      </div>

      <div className="billing-ledger">
        <div className="billing-ledger-head">
          <h3>{filters.status ? `${text.invoices.ledger.heading} — ${text.invoices.statusCards[filters.status]}` : `${text.invoices.ledger.heading} — ${text.invoices.ledger.headingAll}`}</h3>
        </div>
        {loading ? (
          <div className="billing-loading">{text.common.loading}</div>
        ) : ledger.data.length === 0 ? (
          <div className="billing-empty-note">{text.invoices.ledger.empty}</div>
        ) : (
          ledger.data.map((group) => (
            <div key={group.storeId} className={`billing-ledger-group ${openGroups.has(group.storeId) ? "open" : ""}`}>
              <button type="button" className="billing-ledger-group-head" onClick={() => toggleGroup(group.storeId)}>
                <div>
                  <strong>{group.restaurant?.businessName || group.storeId}</strong>
                  <Tags variant="info">
                    {group.invoiceCount} {group.invoiceCount === 1 ? text.invoices.ledger.invoiceCountLabelOne : text.invoices.ledger.invoiceCountLabel}
                  </Tags>
                </div>
                <strong>{formatSar(group.totalMinor, lang)}</strong>
              </button>
              {openGroups.has(group.storeId) && (
                <div className="billing-ledger-group-body">
                  {group.invoices.map((invoice) => (
                    <div key={invoice._id} className="billing-ledger-invoice">
                      <div className="billing-ledger-invoice-main">
                        <div className="billing-ledger-invoice-title">
                          {invoice.pricingSnapshot?.planCode
                            ? `${text.subscriptions.plan} ${invoice.pricingSnapshot.planCode}`
                            : invoice.invoiceNumber || text.invoices.draft}
                        </div>
                        <div className="billing-ledger-invoice-meta">
                          <span>{invoice.invoiceNumber || text.invoices.draft}</span>
                          <span>{formatDate(invoice.issueDate, lang)}</span>
                        </div>
                      </div>
                      <div className="billing-ledger-invoice-money">
                        <strong>{formatSar(invoice.totalMinor, lang)}</strong>
                        {invoice.balanceDueMinor > 0 && invoice.bucket !== "cancelled" && (
                          <small>
                            {text.invoices.ledger.remaining} {formatSar(invoice.balanceDueMinor, lang)}
                          </small>
                        )}
                      </div>
                      <div className="billing-ledger-invoice-status">
                        <Tags variant={STATUS_BADGE[invoice.bucket] || "neutral"}>{text.invoices.statusCards[invoice.bucket] || invoice.bucket}</Tags>
                        <small>
                          {text.invoices.ledger.due} {formatDate(invoice.dueDate, lang)}
                        </small>
                      </div>
                      <div className="billing-ledger-invoice-actions">
                        <Button size="sm" appearance="outline" onClick={() => openDetail(invoice._id)}>
                          {text.invoices.view}
                        </Button>
                        {invoice.invoiceNumber && (
                          <Button
                            size="sm"
                            appearance="outline"
                            loading={downloadingId === invoice._id}
                            onClick={() => exportPdf(invoice)}
                          >
                            {text.invoices.exportPdf}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {group.hasMore && (
                    <Button size="sm" appearance="ghost" onClick={() => viewMore(group)}>
                      {text.invoices.ledger.viewMore}
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
        <div className="billing-plan-footer">
          <span>
            {ledger.pagination.total} {text.common.records}
          </span>
          <div>
            <Button
              size="sm"
              appearance="outline"
              disabled={ledger.pagination.page <= 1}
              onClick={() => runFilters({ page: ledger.pagination.page - 1 })}
            >
              {text.common.previous}
            </Button>
            <span>
              {text.common.page} {ledger.pagination.page} {text.common.of} {ledger.pagination.pages}
            </span>
            <Button
              size="sm"
              appearance="outline"
              disabled={ledger.pagination.page >= ledger.pagination.pages}
              onClick={() => runFilters({ page: ledger.pagination.page + 1 })}
            >
              {text.common.next}
            </Button>
          </div>
        </div>
      </div>

      {detailInvoice && (
        <InvoiceDetailModal
          invoice={detailInvoice}
          onClose={() => setDetailInvoice(null)}
          onUpdated={(updated) => {
            setDetailInvoice(updated);
            runFilters();
          }}
        />
      )}
    </section>
  );
};

export default InvoicesTab;
